import { privateApi } from "@core/libs/axios/apiInstances";
import { handleResponse } from "@core/libs/axios/serviceHandler";
import type { ShapeAnnotation } from "@modules/docs/annotations/types/TAnnotation";
import type {
  BackendAnnotationRecord,
  BackendAnnotationMutationPayload,
  BackendNoteRecord,
} from "@modules/docs/annotations/types/TBackendAnnotation";
import { fromBackendRecords } from "@modules/docs/annotations/utils/annotationMappers";

// ─── GET ─────────────────────────────────────────────────────────────────────
export async function getApprovalAnnotations(
  workspaceId: string,
  projectId: string,
  approvalId: string,
  signal?: AbortSignal,
): Promise<ShapeAnnotation[]> {
  const records = await handleResponse<BackendAnnotationRecord[]>(
    privateApi.get(
      `/documents/${workspaceId}/${projectId}/approvals/${approvalId}/annotations`,
      { signal },
    ),
  );
  return fromBackendRecords(records);
}

// ─── POST (mutation — no signal) ─────────────────────────────────────────────
export async function saveApprovalAnnotations(
  workspaceId: string,
  projectId: string,
  approvalId: string,
  payload: BackendAnnotationMutationPayload,
): Promise<ShapeAnnotation[]> {
  const records = await handleResponse<BackendAnnotationRecord[]>(
    privateApi.post(
      `/documents/${workspaceId}/${projectId}/approvals/${approvalId}/annotations/save`,
      payload,
    ),
  );
  return fromBackendRecords(records);
}

// ─── POST note (mutation — no signal) ────────────────────────────────────────
export async function addAnnotationNote(
  workspaceId: string,
  projectId: string,
  approvalId: string,
  annotationId: string,
  content: string,
): Promise<void> {
  await handleResponse<void>(
    privateApi.post(
      `/documents/${workspaceId}/${projectId}/approvals/${approvalId}/annotations/${annotationId}/notes`,
      { content },
    ),
  );
}

// ─── DELETE note (mutation — no signal) ──────────────────────────────────────
export async function removeAnnotationNote(
  workspaceId: string,
  projectId: string,
  approvalId: string,
  annotationId: string,
  noteId: string,
): Promise<void> {
  await handleResponse<void>(
    privateApi.delete(
      `/documents/${workspaceId}/${projectId}/approvals/${approvalId}/annotations/${annotationId}/notes/${noteId}`,
    ),
  );
}

// ─── GET notes ────────────────────────────────────────────────────────────────
export async function getAnnotationsNotes(
  workspaceId: string,
  projectId: string,
  approvalId: string,
  annotationId: string,
  signal?: AbortSignal,
): Promise<BackendNoteRecord[]> {
  type NotesResponse = { annotation: BackendAnnotationRecord; notes: BackendNoteRecord[] };
  const result = await handleResponse<NotesResponse>(
    privateApi.get(
      `/documents/${workspaceId}/${projectId}/approvals/${approvalId}/annotations/${annotationId}/notes`,
      { signal },
    ),
  );
  return result.notes;
}
